import { Link, useLocation } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { AdminNavBadge } from './AdminNavBadge';

interface AdminSidebarProps {
  className?: string;
}

interface NavItem {
  label: string;
  path: string;
  /** When set, render a pending-count badge on this item. */
  badge?: 'moderation' | 'feedback';
}

export const AdminSidebar = ({ className = '' }: AdminSidebarProps) => {
  const location = useLocation();

  const navItems: NavItem[] = [
    { label: 'Dashboard', path: '/admin/dashboard' },
    { label: 'Content Management', path: '/admin/content' },
    { label: 'User Management', path: '/admin/users' },
    { label: 'Review Moderation', path: '/admin/moderation', badge: 'moderation' },
    { label: 'Feedback', path: '/admin/feedback', badge: 'feedback' },
    { label: 'Settings', path: '/admin/settings' },
  ];

  const moderationCount = trpc.moderation.pendingCount.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });
  const feedbackCount = trpc.feedback.pendingCount.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });

  const isActiveRoute = (path: string) => location.pathname === path;

  const getNavLinkClasses = (path: string) => {
    const baseClasses =
      'flex items-center justify-between px-4 py-2 text-sm font-medium rounded-md transition-colors';
    const activeClasses = 'bg-primary-100 text-primary-700';
    const inactiveClasses = 'text-gray-700 hover:bg-gray-100 hover:text-gray-900';
    return `${baseClasses} ${isActiveRoute(path) ? activeClasses : inactiveClasses}`;
  };

  const countFor = (badge: NavItem['badge']) => {
    if (badge === 'moderation') return moderationCount.data?.count;
    if (badge === 'feedback') return feedbackCount.data?.count;
    return undefined;
  };

  return (
    <div className={`w-64 bg-gray-50 border-r border-gray-200 ${className}`}>
      <div className="flex flex-col h-full">
        <div className="px-4 py-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Admin Dashboard</h2>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={getNavLinkClasses(item.path)}>
              <span>{item.label}</span>
              {item.badge && (
                <AdminNavBadge
                  count={countFor(item.badge)}
                  label={item.badge === 'moderation' ? 'moderation' : 'feedback'}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-gray-200">
          <Link
            to="/"
            className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md transition-colors"
          >
            ← Return to Public Site
          </Link>
        </div>
      </div>
    </div>
  );
};
